import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Tabs, Table, Card, message, Input, Modal, Button } from "antd";
import {
    SearchOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getZohoClientID, patientList, linkDoctorToPatient, patientbyDoctor } from "../../redux/doctorSlice";
import { useNavigate } from "react-router-dom";
import { FaHospital } from "react-icons/fa";

// const SwitchWrapper = ({ onChange, ...props }) => {
//     const handleClick = (e) => {
//         e.stopPropagation();
//     };

//     return (
//         <div onClick={handleClick}>
//             <Switch {...props} onChange={onChange} />
//         </div>
//     );
// };

export default function DoctorDash() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState("2");
    const [showAssignmentModal, setShowAssignmentModal] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);

    const loggedInUserId = useSelector(
        (state) => state?.authentication?.userAuth?.obj?.id,
    );

    const [allData, setAllData] = useState([]);

    //const [data, setData] = useState([]); // Data for the current page
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });
    const [sortConfig, setSortConfig] = useState({
        sortField: null,
        sortOrder: null,
    });
    const userAuth = useSelector(
        (state) => state?.authentication?.userAuth
    );


    const fetchPatientList = useCallback(async () => {
        setLoading(true);
        const params = { page: 0, size: 500 };

        try {
            let response;

            if (activeTab === "1") {
                response = await dispatch(patientList(params));
            } else {
                response = await dispatch(patientbyDoctor(loggedInUserId));
            }

            if (
                (activeTab === "1" && patientList.fulfilled.match(response)) ||
                (activeTab === "2" && patientbyDoctor.fulfilled.match(response))
            ) {
                const updatedList = response?.payload?.data?.map((item) => {
                    if (item && item.createdById === loggedInUserId) {
                        item.createdBy = "You";
                    }
                    return item;
                });

                setAllData(updatedList);
                setPagination((prev) => ({
                    ...prev,
                    total: updatedList?.length,
                }));
            }
        } catch (error) {
            console.error("Error fetching patients:", error);
            message.error("Error fetching patient list.");
        } finally {
            setLoading(false);
        }
    }, [dispatch, activeTab, loggedInUserId]);


    useEffect(() => {
        fetchPatientList();
    }, [fetchPatientList, activeTab]);


    useEffect(() => {
        dispatch(getZohoClientID()).then((response) => {
            if (getZohoClientID.fulfilled.match(response)) {
                console.log("Zoho Client Id-", response.payload);
                localStorage.setItem("zohoClientId", JSON.stringify(response.payload));
            } else {
                console.error("Failed to fetch the Zoho client Id-", response.error.message);
            }
        });
    }, [dispatch]);

    useEffect(() => {
        setPagination({
            current: 1,
            pageSize: 20,
            total: 0,
        });
        setSearchQuery(""); // optional: clear search too
    }, [activeTab]);
    

    const handleTableChange = useCallback((newPagination, filters, sorter) => {
        const { current, pageSize } = newPagination;
        const s = Array.isArray(sorter) ? sorter[0] : sorter;
        const field = s?.field ?? s?.columnKey;
        const order = s?.order ?? null;

        setPagination((prev) => ({
            ...prev,
            current,
            pageSize,
        }));

        setSortConfig({
            sortField: order ? field : null,
            sortOrder: order,
        });
    }, []);
    const handleSearch = useCallback((value) => {
        setSearchQuery(value);
    }, []);

    const dataWithIds = useMemo(
        () =>
            allData?.map((item) => ({
                ...item,
                id: item.id,
            })),
        [allData], // changed dependency to allData
    );

    const filteredData = useMemo(() => {
        if (!searchQuery) return dataWithIds;
        console.log({ dataWithIds })
        return dataWithIds.filter((item) =>
            item.firstname.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.lastname.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (item.flag && item.flag.toString().toLowerCase().includes(searchQuery.toLowerCase()))
        );
    }, [searchQuery, dataWithIds]);

    const sortedData = useMemo(() => {
        const { sortField, sortOrder } = sortConfig;
        if (!sortField || !sortOrder) {
            return filteredData;
        }
        const list = [...(filteredData || [])];
        const dir = sortOrder === "ascend" ? 1 : -1;
        list.sort((a, b) => {
            let av = a[sortField];
            let bv = b[sortField];
            if (av == null && bv == null) return 0;
            if (av == null) return 1 * dir;
            if (bv == null) return -1 * dir;
            if (typeof av === "number" && typeof bv === "number") {
                return dir * (av - bv);
            }
            const aStr = String(av).toLowerCase();
            const bStr = String(bv).toLowerCase();
            return dir * aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: "base" });
        });
        return list;
    }, [filteredData, sortConfig]);

    const columns = useMemo(() => {
        const baseColumns = [
            {
                title: "First Name",
                dataIndex: "firstname",
                key: "firstname",
                sorter: true,
                sortOrder: sortConfig.sortField === "firstname" ? sortConfig.sortOrder : null,
            },
            {
                title: "Last Name",
                dataIndex: "lastname",
                key: "lastname",
                sorter: true,
                sortOrder: sortConfig.sortField === "lastname" ? sortConfig.sortOrder : null,
            },
            {
                title: "Gender",
                dataIndex: "gender",
                key: "gender",
                sorter: true,
                sortOrder: sortConfig.sortField === "gender" ? sortConfig.sortOrder : null,
                render: (gender) => gender || "-",
            },
            {
                title: "Email",
                dataIndex: "email",
                key: "email",
                sorter: true,
                sortOrder: sortConfig.sortField === "email" ? sortConfig.sortOrder : null,
                render: (email) => (
                    <div>
                        {email}
                    </div>
                ),
            },
            {
                title: "Clinician",
                dataIndex: "providers",
                key: "clinician",
                render: (providers) => {
                    if (!Array.isArray(providers) || providers.length === 0) return "-";

                    return (
                        <div>
                            {providers.map((provider, index) => (
                                <div key={index} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                                    <span>{provider.providerName}</span>

                                    {provider.statusRemark && (
                                        <span style={{ width: "12px", height: "12px", backgroundColor: provider.statusRemark.toLowerCase() === "approved" ? "green" : "#95de64", borderRadius: "50%", display: "inline-block" }}></span>

                                    )}
                                </div>

                            ))}
                        </div>
                    );
                },
            },
        ];

        if (activeTab === "1") {
            baseColumns.push({
                title: "Assigned to me",
                dataIndex: "assignedToMe",
                key: "assignedToMe",
                render: (_, record) => {
                    const providers = Array.isArray(record.providers) ? record.providers : [];
                    const fullName = `${userAuth?.obj?.firstName} ${userAuth?.obj?.lastName}`;

                    const isAssignedToMe = providers.some(
                        (provider) => provider?.providerName === fullName
                    );

                    return (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <button
                                className="isAssignBtn ant-btn css-dev-only-do-not-override-f7vrd6 ant-btn-primary"
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                        const payload = {
                                            patientRef: record.userRef,
                                            link: !isAssignedToMe,  // false if already assigned (Un-Assign), true otherwise
                                        };
                                
                                        await dispatch(linkDoctorToPatient(payload));
                                        message.success(isAssignedToMe ? "Patient unassigned successfully." : "Patient added to your care - pending admin review.");
                                        await fetchPatientList();
                                
                                        setAllData((prevData) =>
                                            prevData.map((item) =>
                                                item.userRef === record.userRef
                                                    ? {
                                                        ...item,
                                                        providers: !isAssignedToMe
                                                            ? [
                                                                ...(item.providers || []).filter(
                                                                    (provider) => provider.providerName !== fullName
                                                                ),
                                                                {
                                                                    userRef: loggedInUserId,
                                                                    providerName: fullName,
                                                                    status: 0,
                                                                    statusRemark: "Pending",
                                                                },
                                                            ]
                                                            : (item.providers || []).filter(
                                                                (provider) => provider.providerName !== fullName
                                                            ),
                                                    }
                                                    : item
                                            )
                                        );
                                
                                    } catch (error) {
                                        console.error(error);
                                        message.error(`Failed to ${isAssignedToMe ? "unassign" : "assign"} patient.`);
                                    }
                                }}
                                
                            >
                                {isAssignedToMe ? "Un-Assign" : "Assign"}
                            </button>
                        </div>
                    );
                },
            });
        }


        return baseColumns;
    }, [
        activeTab,
        dispatch,
        loggedInUserId,
        fetchPatientList,
        userAuth?.obj?.firstName,
        userAuth?.obj?.lastName,
        sortConfig.sortField,
        sortConfig.sortOrder,
    ]);



    const isPatientAssignedToMe = useCallback((patient) => {
        const providers = Array.isArray(patient.providers) ? patient.providers : [];
        const fullName = `${userAuth?.obj?.firstName} ${userAuth?.obj?.lastName}`;
        return providers.some(
            (provider) => provider?.providerName === fullName
        );
    }, [userAuth?.obj?.firstName, userAuth?.obj?.lastName]);

    const handlePatientRowClick = useCallback((record) => {
        if (activeTab === "2") {
            localStorage.setItem("patient", JSON.stringify(record));
            navigate(`/doctor/user/${record.userRef}`, {
                state: { user: record },
            });
        } else {
            if (isPatientAssignedToMe(record)) {
                localStorage.setItem("patient", JSON.stringify(record));
                navigate(`/doctor/user/${record.userRef}`, {
                    state: { user: record },
                });
            } else {
                setSelectedPatient(record);
                setShowAssignmentModal(true);
            }
        }
    }, [activeTab, navigate, isPatientAssignedToMe]);

    const PatientList = React.memo(
        () => (
            <>
                <div className="status-indicators">
                    <div className="status-item">
                        <span className="status-dot approved"></span>
                        <span>Approved</span>
                    </div>
                    <div className="status-item">
                        <span className="status-dot pending"></span>
                        <span>Recently assigned</span>
                    </div>
                </div>

                <Card>
                    <Table
                        columns={columns}
                        dataSource={sortedData?.slice(
                            (pagination.current - 1) * pagination.pageSize,
                            pagination.current * pagination.pageSize
                        )}

                        loading={loading}
                        pagination={{
                            position: ['bottomRight'],
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: sortedData?.length ?? 0,
                            showSizeChanger: true,
                        }}
                        onChange={handleTableChange}
                        rowKey="id"
                        scroll={{ x: "max-content" }}
                        onRow={(record) => ({
                            onClick: (e) => {
                                if (!e.target.closest(".isAssignBtn")) {
                                    handlePatientRowClick(record);
                                }
                            },
                        })}
                        
                    />
                </Card>
            </>
        ),
        [columns, sortedData, loading, handleTableChange, navigate, pagination, handlePatientRowClick],
    );

    const items = [
        {
            label: "All Patients",
            key: "1",
            children: <PatientList />,
        },
        {
            label: "Assigned to me",
            key: "2",
            children: <PatientList />,
        },
    ];

    return (
        <Row gutter={16} style={{ padding: "0 5%" }}>
            <Col xs={24} md={24} style={{ paddingBottom: "16px" }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                        gap: 16,
                        flexWrap: "wrap",
                        padding: "6px 0 14px",
                    }}
                >
                    <div style={{ minWidth: 220 }}>
                        <p style={{ color: "#335CAD", fontWeight: 800, fontSize: 18, margin: 0 }}>
                            Patient List
                        </p>
                        <p style={{ margin: "6px 0 0", color: "#6b7280", fontSize: 12 }}>
                            Search and manage your patients.
                        </p>
                    </div>

                    <Button
                        type="primary"
                        onClick={() => navigate("/doctor/zoom-availability")}
                        style={{
                            height: 42,
                            padding: "0 16px",
                            borderRadius: 12,
                            fontWeight: 700,
                            background: "#01ADF0",
                            borderColor: "#01ADF0",
                            boxShadow: "0 10px 24px rgba(1, 173, 240, 0.25)",
                        }}
                    >
                        Set Zoom availability
                    </Button>
                </div>

                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => {
                        setActiveTab(key);
                        setSearchQuery(""); // Optional: reset search when changing tabs
                    }}
                    items={items}
                    tabBarExtraContent={
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                flexWrap: "wrap",
                                paddingBottom: 8,
                            }}
                        >
                            <div style={{ minWidth: 220, flex: "1 1 260px" }}>
                                <Input
                                    placeholder="Search patients"
                                    value={searchQuery}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    prefix={<SearchOutlined />}
                                    style={{ borderRadius: 10, height: 40 }}
                                />
                            </div>
                        </div>
                    }
                />

            </Col>

            {/* Assignment Required Modal */}
            <Modal
                title={null}
                open={showAssignmentModal}
                onCancel={() => setShowAssignmentModal(false)}
                footer={null}
                width={600}
                centered
                closable={false}
                maskClosable={false}
            >
                <div style={{ 
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 32px rgba(51, 92, 173, 0.08)',
                }}>
                    {/* Header */}
                    <div style={{
                        background: 'linear-gradient(135deg, #00ADEF 0%, #01acee 100%)',
                        padding: '24px 32px',
                        textAlign: 'center',
                        position: 'relative',
                    }}>
                        {/* Icon */}
                        <div style={{
                            width: '64px',
                            height: '64px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px',
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                        }}>
                            <span style={{ fontSize: '32px' }}><FaHospital style={{ color: '#fff' }} /></span>
                        </div>
                        
                        <h2 style={{
                            margin: 0,
                            color: '#fff',
                            fontSize: '24px',
                            fontWeight: '700',
                            textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                        }}>
                            Patient Assignment Required
                        </h2>
                    </div>
                    
                    {/* Content */}
                    <div style={{ padding: '32px' }}>
                        {/* Info Box */}
                        <div style={{
                            background: 'rgba(51, 92, 173, 0.05)',
                            borderRadius: '12px',
                            padding: '20px',
                            marginBottom: '24px',
                            border: '1px solid rgba(51, 92, 173, 0.1)',
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '16px',
                            }}>
                                <div style={{
                                    width: '24px',
                                    height: '24px',
                                    background: '#335CAD',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '12px',
                                }}>
                                    <span style={{ color: '#fff', fontSize: '12px' }}>ℹ️</span>
                                </div>
                                <span style={{
                                    color: '#335CAD',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                }}>
                                    Assignment Needed
                                </span>
                            </div>
                            <p style={{
                                margin: 0,
                                color: '#666',
                                fontSize: '14px',
                                lineHeight: '1.5',
                            }}>
                                Assign yourself to this patient to view their record - access is active right away while admin review completes in the background.
                            </p>
                        </div>

                        {/* Steps */}
                        <div style={{ marginBottom: '24px' }}>
                            <h3 style={{
                                margin: '0 0 16px 0',
                                color: '#222',
                                fontSize: '18px',
                                fontWeight: '600',
                            }}>
                                To view this patient's information:
                            </h3>
                            
                          
                            
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px 16px',
                                background: '#fff',
                                borderRadius: '8px',
                                border: '1px solid #E8F4FD',
                            }}>
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    background: 'linear-gradient(135deg, #335CAD 0%, #00ADEF 100%)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '16px',
                                    color: '#fff',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                }}>
                                    1
                                </div>
                                <span style={{
                                    color: '#222',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }}>
                                    Assign the patient to yourself
                                </span>
                            </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div style={{
                            display: 'flex',
                            gap: '16px',
                            justifyContent: 'center',
                        }}>
                            <Button
                                size="large"
                                onClick={() => setShowAssignmentModal(false)}
                                style={{
                                    padding: '12px 32px',
                                    borderRadius: '8px',
                                    border: '2px solid #E8F4FD',
                                    color: '#666',
                                    fontWeight: '500',
                                    fontSize: '14px',
                                }}
                            >
                                Cancel
                            </Button>
                            
                            <Button
                                type="primary"
                                size="large"
                                onClick={() => {
                                    setShowAssignmentModal(false);
                                    // Navigate to the patient and show the assign button
                                    if (selectedPatient) {
                                        localStorage.setItem("patient", JSON.stringify(selectedPatient));
                                        navigate(`/doctor/user/${selectedPatient.userRef}`, {
                                            state: { user: selectedPatient },
                                        });
                                    }
                                }}
                                style={{
                                    background: 'linear-gradient(135deg, #335CAD 0%, #00ADEF 100%)',
                                    border: 'none',
                                    padding: '12px 32px',
                                    borderRadius: '8px',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    boxShadow: '0 4px 12px rgba(51, 92, 173, 0.3)',
                                }}
                            >
                                Go to Patient
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
        </Row>
    );
}
